import { useEffect, useState, useCallback } from 'react'
import {
  Bug, Plus, ThumbsUp, MessageSquare, Pin, X,
  Loader2, ArrowLeft, Send, Trash2, ShieldAlert, ChevronDown,
  Image as ImageIcon, CheckCircle2, Pencil, RotateCcw, Save, User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  bugsList, bugsGet, bugsCreate, bugsVote, bugsComment,
  bugsDeleteOwn, bugsDeleteOwnComment, bugsUpdateOwn, bugsReopen,
  bugsAdminPatch, bugsAdminDelete, bugsAdminDeleteComment,
  Bug as BugType, BugComment, BugStatus, BugSeverity,
} from '../api'
import { ScreenshotUploader, ScreenshotGallery } from './IdeasPage'

const MAX_SHOTS_BUG = 5
const MAX_SHOTS_COMMENT = 3

const CATEGORIES: Record<string, string> = {
  browser:     'Браузер',
  panel:       'Панель',
  proxy:       'Прокси',
  captcha:     'Капча',
  vpn:         'VPN',
  chrome:      'Chrome',
  fingerprint: 'Отпечатки',
  deploy:      'Деплой',
  other:       'Другое',
}

const STATUS_META: Record<BugStatus, { label: string; cls: string }> = {
  new:         { label: 'Новый',        cls: 'bg-primary/10 text-primary border-primary/30' },
  confirmed:   { label: 'Подтверждён',  cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  in_progress: { label: 'В работе',     cls: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  fixed:       { label: 'Исправлен',    cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  resolved:    { label: 'Решён',        cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  wont_fix:    { label: 'Не будет',     cls: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
  duplicate:   { label: 'Дубликат',     cls: 'bg-muted text-muted-foreground border-border' },
}

const SEVERITY_META: Record<BugSeverity, { label: string; cls: string }> = {
  low:      { label: 'Низкий',     cls: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
  medium:   { label: 'Средний',    cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  high:     { label: 'Высокий',    cls: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  critical: { label: 'Критический',cls: 'bg-red-500/10 text-red-400 border-red-500/30' },
}

const SORT_OPTIONS = [
  { id: 'popular',   label: 'Популярные' },
  { id: 'new',       label: 'Новые' },
  { id: 'discussed', label: 'Обсуждаемые' },
  { id: 'old',       label: 'Старые' },
]

function formatDate(ts: number): string {
  try {
    const d = new Date(ts * 1000)
    const now = Date.now()
    const diff = (now - d.getTime()) / 1000
    if (diff < 60) return 'только что'
    if (diff < 3600) return `${Math.floor(diff / 60)} мин. назад`
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч. назад`
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

function getBrowserInfo(): string {
  try {
    const ua = navigator.userAgent
    // Trim to something sane
    return ua.length > 200 ? ua.slice(0, 200) : ua
  } catch { return '' }
}

interface BugsPageProps {
  selectedId?: number | null
  onSelectChange?: (id: number | null) => void
  onTitleResolved?: (title: string) => void
  // When true (super-admin), the detail view shows extra moderation
  // controls (status change, hide, hard delete) that hit the same
  // /api/bugs-admin/* endpoints used by the dedicated admin page.
  isSuperAdmin?: boolean
  // Когда true — создание багов запрещено для лицензии (с лиц-сервера).
  // Кнопку «Сообщить о баге» прячем; серверный гейт всё равно вернёт 403.
  restricted?: boolean
}

/**
 * Render the author identity for a bug/comment. Falls back to the
 * user-entered label for normal viewers, but for super-admins we
 * surface the real `panel_username` and the panel URL (subdomain.crp.pro)
 * so the maintainer can immediately tell which client server to debug.
 */
function renderBugAuthor(item: any, isSuperAdmin: boolean): React.ReactNode {
  const c = item?.client
  if (isSuperAdmin && c) {
    const username = (c.panel_username || '').trim()
    const sub = username ? `${username}.crp.pro` : (c.hostname || '').trim()
    return (
      <span className="inline-flex flex-wrap items-center gap-x-1">
        {username && <span className="text-primary font-medium">@{username}</span>}
        {!username && sub && <span className="text-primary font-medium">{sub}</span>}
        {sub && username && (
          <a href={`https://${sub}`} target="_blank" rel="noreferrer"
             className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
             onClick={(e) => e.stopPropagation()}
             title="Открыть панель клиента">
            {sub}
          </a>
        )}
        {item.author_label && (
          <span className="text-muted-foreground italic">"{item.author_label}"</span>
        )}
      </span>
    )
  }
  return <>{item.author_label || 'анон'}</>
}

export default function BugsPage({ selectedId: controlledId, onSelectChange, onTitleResolved, isSuperAdmin = false, restricted = false }: BugsPageProps = {}) {
  const [bugs, setBugs] = useState<BugType[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('new')
  const [filterCategory, setFilterCategory] = useState<string>('')
  const [filterSeverity, setFilterSeverity] = useState<string>('')
  const [sort, setSort] = useState<string>('popular')
  // Idea #79: "Мои" — show only bugs authored by the current client.
  // When active we drop the status filter so the user sees ALL their bugs
  // (across statuses), then filter to is_mine on the client.
  const [mine, setMine] = useState(false)
  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(null)
  const selectedId = controlledId !== undefined ? controlledId : internalSelectedId
  const setSelectedId = useCallback((id: number | null) => {
    if (onSelectChange) onSelectChange(id)
    else setInternalSelectedId(id)
  }, [onSelectChange])
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await bugsList({
        status: mine ? undefined : (filterStatus || undefined),
        category: filterCategory || undefined,
        severity: filterSeverity || undefined,
        sort,
      })
      setBugs(r.bugs || [])
    } catch { /* ignore */ }
    setLoading(false)
  }, [filterStatus, filterCategory, filterSeverity, sort, mine])

  useEffect(() => { void load() }, [load])

  const displayed = mine ? bugs.filter(b => b.is_mine) : bugs

  const onVote = async (id: number, value: 0 | 1) => {
    try {
      const r = await bugsVote(id, value)
      setBugs(cur => cur.map(x => x.id === id ? { ...x, votes_up: r.votes_up, score: r.votes_up, my_vote: r.my_vote } : x))
    } catch { /* ignore */ }
  }

  if (selectedId !== null) {
    return <BugDetailPage id={selectedId} onBack={() => { setSelectedId(null); void load() }} onTitleResolved={onTitleResolved} isSuperAdmin={isSuperAdmin} />
  }

  return (
    <div className="p-5 max-w-5xl">
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
          <Bug className="w-5 h-5 text-red-400" />
          Баги и ошибки
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Сообщайте о багах, голосуйте за важные, описывайте сценарий.
          Разработчики видят приоритет по количеству «+1».
        </p>
        {!restricted && (
          <div className="mt-3">
            <Button onClick={() => setCreating(true)} className="gap-2">
              <Plus className="w-4 h-4" /> Сообщить о баге
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setMine(m => !m)}
          title="Показать только мои баги"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition-colors ${
            mine
              ? 'bg-primary/15 border-primary/40 text-primary'
              : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/30'
          }`}
        >
          <User className="w-3.5 h-3.5" /> Мои
        </button>
        <Select
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder="Новый"
          options={Object.entries(STATUS_META).map(([id, m]) => ({ id, label: m.label }))}
        />
        <Select
          value={filterCategory}
          onChange={setFilterCategory}
          placeholder="Все категории"
          options={[
            { id: '', label: 'Все категории' },
            ...Object.entries(CATEGORIES).map(([id, label]) => ({ id, label })),
          ]}
        />
        <Select
          value={filterSeverity}
          onChange={setFilterSeverity}
          placeholder="Любой приоритет"
          options={[
            { id: '', label: 'Любой приоритет' },
            ...Object.entries(SEVERITY_META).map(([id, m]) => ({ id, label: m.label })),
          ]}
        />
        <div className="ml-auto">
          <Select value={sort} onChange={setSort} options={SORT_OPTIONS} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ShieldAlert className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="text-lg mb-2">{mine ? 'У вас пока нет багов' : 'Пока нет багов'}</p>
          <p className="text-sm">{mine ? 'Здесь появятся баги, которые вы создали.' : 'Нашли проблему? Сообщите — и мы починим!'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {displayed.map(bug => (
            <BugCard key={bug.id} bug={bug}
                     onClick={() => setSelectedId(bug.id)}
                     onVote={onVote}
                     isSuperAdmin={isSuperAdmin} />
          ))}
        </div>
      )}

      {creating && <NewBugModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void load() }} />}
    </div>
  )
}


function BugCard({ bug, onClick, onVote, isSuperAdmin = false }: {
  bug: BugType
  onClick: () => void
  onVote: (id: number, value: 0 | 1) => void
  isSuperAdmin?: boolean
}) {
  const sm = STATUS_META[bug.status] || { label: bug.status || '?', cls: 'bg-muted text-muted-foreground border-border' }
  const sev = SEVERITY_META[bug.severity]
  const isResolved = bug.status === 'fixed' || bug.status === 'wont_fix' || bug.status === 'duplicate'
  return (
    <div onClick={onClick}
         className={`rounded-xl border p-4 space-y-2 transition-colors cursor-pointer relative overflow-hidden ${
           isResolved
             ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] to-card hover:border-emerald-500/50'
             : 'border-border bg-card hover:border-red-500/40'
         }`}>
      {isResolved && (
        <CheckCircle2 className="absolute bottom-3 right-3 w-8 h-8 text-emerald-500/20" />
      )}
      {bug.pinned && !isResolved && (
        <Pin className="absolute top-3 right-3 w-3.5 h-3.5 text-red-400 rotate-45" />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Bug number — for easy mention in chats: "баг #43 надо пофиксить" */}
        <span className="text-[11px] font-mono font-bold tabular-nums text-foreground/80 bg-muted/60 px-1.5 py-0.5 rounded border border-border/50">
          #{bug.id}
        </span>
        <Badge variant="outline" className={`text-[10px] h-5 ${sm.cls}`}>{sm.label}</Badge>
        {sev && <Badge variant="outline" className={`text-[10px] h-5 ${sev.cls}`}>{sev.label}</Badge>}
        <span className="text-[10px] text-muted-foreground">
          {CATEGORIES[bug.category] || bug.category}
        </span>
        {(bug as any).has_team_reply && (
          <Badge variant="outline" className="text-[10px] h-5 border-primary/40 text-primary bg-primary/10">
            ✓ Ответ команды
          </Badge>
        )}
      </div>
      <h3 className="text-sm font-semibold line-clamp-2">{bug.title}</h3>
      {(() => {
        // Strip private PROJECT_URL marker from card preview too — the
        // detail view will show it (admin-only). Card just gets clean text.
        const cleanDesc = (bug.description || '').replace(/\s*<!--PROJECT_URL:.+?-->\s*/g, '').trim()
        return cleanDesc && (
          <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
            {cleanDesc}
          </p>
        )
      })()}
      {bug.affects_version && (
        <div className="text-[10px] text-muted-foreground">
          версия: <span className="font-mono text-foreground/70">{bug.affects_version}</span>
        </div>
      )}
      {bug.screenshots && bug.screenshots.length > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <ImageIcon className="w-3 h-3" />
          <span>{bug.screenshots.length}</span>
        </div>
      )}
      {isSuperAdmin && (
        <div className="text-[10px] text-muted-foreground pt-0.5 truncate">
          {renderBugAuthor(bug, true)}
        </div>
      )}
      <div className="flex items-center gap-3 pt-1 text-xs">
        <button
          onClick={(e) => { e.stopPropagation(); onVote(bug.id, bug.my_vote === 1 ? 0 : 1) }}
          className={`flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
            bug.my_vote === 1
              ? 'bg-red-500/20 border-red-500/40 text-red-400'
              : 'border-border hover:border-red-500/30 hover:text-red-400'
          }`}
          title="+1 у меня тоже"
        >
          <ThumbsUp className="w-3.5 h-3.5" /> {bug.votes_up}
        </button>
        {bug.comments_count > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground ml-auto">
            <MessageSquare className="w-3.5 h-3.5" /> {bug.comments_count}
          </span>
        )}
      </div>
    </div>
  )
}


function BugDetailPage({ id, onBack, onTitleResolved, isSuperAdmin = false }: {
  id: number; onBack: () => void; onTitleResolved?: (t: string) => void; isSuperAdmin?: boolean
}) {
  const [bug, setBug] = useState<BugType | null>(null)
  const [comments, setComments] = useState<BugComment[]>([])
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState('')
  const [replyShots, setReplyShots] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  // Idea #35
  const [editing, setEditing] = useState(false)
  const [reopening, setReopening] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await bugsGet(id)
      setBug(r.bug)
      setComments(r.comments)
      if (onTitleResolved && r.bug?.title) onTitleResolved(r.bug.title)
    } catch { /* ignore */ }
    setLoading(false)
  }, [id, onTitleResolved])

  useEffect(() => { void load() }, [load])

  const onVote = async (value: 0 | 1) => {
    if (!bug) return
    try {
      const r = await bugsVote(bug.id, value)
      setBug(cur => cur ? { ...cur, votes_up: r.votes_up, score: r.votes_up, my_vote: r.my_vote } : cur)
    } catch { /* ignore */ }
  }

  const onSend = async () => {
    const t = replyText.trim()
    if ((!t && replyShots.length === 0) || !bug) return
    setSending(true)
    try {
      await bugsComment(bug.id, t || '(скриншот)', undefined, replyShots)
      setReplyText('')
      setReplyShots([])
      await load()
    } catch { /* ignore */ }
    setSending(false)
  }

  const onDeleteComment = async (cid: number) => {
    if (!confirm('Удалить комментарий?')) return
    try {
      await bugsDeleteOwnComment(cid)
      setComments(cur => cur.filter(c => c.id !== cid))
    } catch { /* ignore */ }
  }

  const onDeleteBug = async () => {
    if (!bug) return
    if (!confirm('Удалить свой баг-репорт? Скрыть для всех?')) return
    try {
      await bugsDeleteOwn(bug.id)
      onBack()
    } catch { /* ignore */ }
  }

  if (loading || !bug) {
    return (
      <div className="p-5 max-w-5xl flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const sm = STATUS_META[bug.status] || { label: bug.status || '?', cls: 'bg-muted text-muted-foreground border-border' }
  const sev = SEVERITY_META[bug.severity]

  return (
    <div className="p-5 max-w-5xl space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
        <ArrowLeft className="w-4 h-4" /> Назад к списку
      </Button>

      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="flex items-start gap-2 flex-wrap">
          <Badge variant="outline" className={`${sm.cls}`}>{sm.label}</Badge>
          {sev && <Badge variant="outline" className={`${sev.cls}`}>{sev.label}</Badge>}
          <span className="text-xs text-muted-foreground py-0.5">
            #{CATEGORIES[bug.category] || bug.category}
          </span>
          {bug.pinned && <Pin className="w-4 h-4 text-red-400 rotate-45" />}
          <span className="text-xs text-muted-foreground ml-auto inline-flex items-center gap-1">
            {renderBugAuthor(bug, isSuperAdmin)}
            <span className="opacity-50">·</span>
            <span>{formatDate(bug.created_at)}</span>
          </span>
        </div>
        <h1 className="text-xl font-semibold tracking-tight flex items-baseline gap-2 flex-wrap">
          <span className="text-base font-mono font-bold tabular-nums text-foreground/60 bg-muted/60 px-2 py-0.5 rounded border border-border/50">
            #{bug.id}
          </span>
          <span>{bug.title}</span>
        </h1>
        {(() => {
          // Extract <!--PROJECT_URL:...--> marker so non-admins don't see
          // the project link the reporter pasted privately for the team.
          const m = (bug.description || '').match(/<!--PROJECT_URL:(.+?)-->/)
          const projectUrl = m ? m[1].trim() : ''
          const cleanDesc = (bug.description || '').replace(/\s*<!--PROJECT_URL:.+?-->\s*/g, '').trim()
          return (
            <>
              {cleanDesc && (
                <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                  {cleanDesc}
                </p>
              )}
              {projectUrl && isSuperAdmin && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 flex items-center gap-2 text-xs">
                  <span className="text-emerald-400 shrink-0">🔒 Проект автора:</span>
                  <a
                    href={projectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate min-w-0"
                  >
                    {projectUrl}
                  </a>
                  <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                    видно только команде
                  </span>
                </div>
              )}
            </>
          )
        })()}

        {/* Meta info */}
        {(bug.affects_version || bug.browser_info) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground pt-1">
            {bug.affects_version && (
              <div>Версия: <span className="font-mono text-foreground/80">{bug.affects_version}</span></div>
            )}
            {bug.browser_info && (
              <div className="max-w-full truncate">Окружение: <span className="font-mono text-foreground/80">{bug.browser_info}</span></div>
            )}
          </div>
        )}

        {/* Structured fields */}
        {bug.steps_to_reproduce && (
          <DetailBlock title="Шаги воспроизведения" body={bug.steps_to_reproduce} />
        )}
        {bug.expected && (
          <DetailBlock title="Ожидаемое поведение" body={bug.expected} />
        )}
        {bug.actual && (
          <DetailBlock title="Фактическое поведение" body={bug.actual} accent="red" />
        )}

        {bug.screenshots && bug.screenshots.length > 0 && (
          <div className="pt-1">
            <ScreenshotGallery names={bug.screenshots} />
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <button onClick={() => onVote(bug.my_vote === 1 ? 0 : 1)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors ${
                    bug.my_vote === 1
                      ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : 'border-border hover:border-red-500/30 hover:text-red-400'
                  }`}
                  title="+1 у меня тоже">
            <ThumbsUp className="w-4 h-4" /> +1 у меня тоже ({bug.votes_up})
          </button>

          {/* Idea #35: edit/reopen actions for bug author */}
          {bug.is_mine && !['fixed', 'wont_fix', 'duplicate'].includes(bug.status) && (
            <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setEditing(true)}>
              <Pencil className="w-3.5 h-3.5" /> Редактировать
            </Button>
          )}
          {['fixed', 'wont_fix', 'duplicate'].includes(bug.status) && (
            <Button variant="outline" size="sm" className="gap-1.5 h-8 border-amber-500/40 text-amber-400 hover:bg-amber-500/10" onClick={() => setReopening(true)}>
              <RotateCcw className="w-3.5 h-3.5" /> Возобновить тикет
            </Button>
          )}

          <span className="ml-auto text-xs text-muted-foreground">
            Голосов: <span className="text-red-400 font-medium">{bug.votes_up}</span>
          </span>
        </div>
      </div>

      {editing && bug && (
        <BugEditModal bug={bug} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); void load() }} />
      )}
      {reopening && (
        <BugReopenDialog
          onClose={() => setReopening(false)}
          onConfirm={async (text) => {
            try {
              await bugsReopen(bug.id, text)
              setReopening(false)
              void load()
            } catch (e: any) {
              alert('Не удалось возобновить: ' + (e?.message || e))
            }
          }}
        />
      )}

      {isSuperAdmin && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-semibold text-amber-300">Действия модератора</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Статус:</span>
            {(Object.keys(STATUS_META) as BugStatus[]).map(s => (
              <button key={s}
                onClick={async () => {
                  if (s === bug.status) return
                  try {
                    await bugsAdminPatch(bug.id, { status: s } as any)
                    setBug(cur => cur ? { ...cur, status: s } : cur)
                  } catch { /* noop */ }
                }}
                className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                  s === bug.status
                    ? STATUS_META[s].cls + ' ring-1 ring-current'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/40'
                }`}
              >{STATUS_META[s].label}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-amber-500/20">
            <button
              onClick={async () => {
                try {
                  await bugsAdminPatch(bug.id, { pinned: !bug.pinned } as any)
                  setBug(cur => cur ? { ...cur, pinned: !cur.pinned } : cur)
                } catch { /* noop */ }
              }}
              className="text-[11px] px-2 py-1 rounded-md border border-border hover:border-primary/40 hover:text-primary"
            >
              {bug.pinned ? 'Открепить' : 'Закрепить'}
            </button>
            <button
              onClick={async () => {
                try {
                  await bugsAdminPatch(bug.id, { hidden: !(bug as any).hidden } as any)
                  setBug(cur => cur ? { ...cur, hidden: !(cur as any).hidden } as any : cur)
                } catch { /* noop */ }
              }}
              className="text-[11px] px-2 py-1 rounded-md border border-border hover:border-amber-500/40 hover:text-amber-400"
            >
              {(bug as any).hidden ? 'Показать всем' : 'Скрыть от всех'}
            </button>
            <button
              onClick={async () => {
                if (!confirm(`Удалить баг #${bug.id} НАВСЕГДА? Это действие необратимо.`)) return
                try {
                  await bugsAdminDelete(bug.id)
                  onBack()
                } catch { /* noop */ }
              }}
              className="ml-auto text-[11px] px-2 py-1 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10"
            >
              Удалить навсегда
            </button>
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Комментарии ({comments.length})
        </h2>

        {comments.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">Пока нет обсуждений. Оставьте первый комментарий.</p>
        )}

        <div className="space-y-2">
          {comments.map(c => (
            <div key={c.id}
                 className={`rounded-lg border p-3 text-sm ${
                   c.is_admin
                     ? 'bg-primary/5 border-primary/30'
                     : 'bg-muted/30 border-border'
                 }`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                {c.is_admin ? (
                  <Badge variant="outline" className="h-5 text-[10px] bg-primary/15 text-primary border-primary/30">Команда</Badge>
                ) : (
                  <span className="font-medium text-foreground/80">{renderBugAuthor(c, isSuperAdmin)}</span>
                )}
                <span>·</span>
                <span>{formatDate(c.created_at)}</span>
                {c.is_mine && (
                  <button onClick={() => onDeleteComment(c.id)}
                          className="ml-auto text-red-400 hover:text-red-300" title="Удалить">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
                {!c.is_mine && isSuperAdmin && (
                  <button onClick={async () => {
                            if (!confirm('Удалить этот комментарий?')) return
                            try {
                              await bugsAdminDeleteComment(c.id)
                              setComments(cur => cur.filter(x => x.id !== c.id))
                            } catch { /* noop */ }
                          }}
                          className="ml-auto text-amber-400 hover:text-amber-300" title="Удалить (модератор)">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="whitespace-pre-wrap leading-relaxed">{c.text}</div>
              {c.screenshots && c.screenshots.length > 0 && (
                <div className="mt-2">
                  <ScreenshotGallery names={c.screenshots} small />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex gap-2">
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Напишите комментарий..."
              rows={2}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[60px]"
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void onSend() }
              }}
            />
            <Button onClick={onSend} disabled={(!replyText.trim() && replyShots.length === 0) || sending} className="self-end gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Отправить
            </Button>
          </div>
          <ScreenshotUploader value={replyShots} onChange={setReplyShots} max={MAX_SHOTS_COMMENT} disabled={sending} />
          <p className="text-[11px] text-muted-foreground">Ctrl+Enter — быстро отправить</p>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button variant="ghost" size="sm" onClick={onDeleteBug}
                className="text-xs text-muted-foreground hover:text-red-400 gap-1.5">
          <Trash2 className="w-3 h-3" /> Удалить свой баг-репорт
        </Button>
      </div>
    </div>
  )
}


function DetailBlock({ title, body, accent }: { title: string; body: string; accent?: 'red' }) {
  const borderCls = accent === 'red' ? 'border-red-500/30' : 'border-border'
  const titleCls = accent === 'red' ? 'text-red-400' : 'text-muted-foreground'
  return (
    <div className={`rounded-lg border ${borderCls} bg-muted/20 px-3 py-2`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${titleCls}`}>{title}</div>
      <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{body}</div>
    </div>
  )
}


function NewBugModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [steps, setSteps] = useState('')
  const [expected, setExpected] = useState('')
  const [actual, setActual] = useState('')
  const [category, setCategory] = useState<string>('other')
  const [severity, setSeverity] = useState<string>('medium')
  const [affectsVersion, setAffectsVersion] = useState('')
  const [browserInfo, setBrowserInfo] = useState(() => getBrowserInfo())
  const [authorLabel, setAuthorLabel] = useState('')
  // Operator request: ссылка на настройки проекта где баг увиден.
  // Префиксуется текущим origin (https://stormdi.crp.pro etc) если
  // оператор сейчас открыт в проекте — мы пытаемся угадать ссылку.
  const [projectUrl, setProjectUrl] = useState(() => {
    // If we're currently viewing a project page (URL contains /project/)
    // pre-fill with the current location.href so reporter doesn't have
    // to copy-paste manually.
    try {
      const href = window.location.href
      if (href.includes('/project/')) return href
    } catch { /* ignore */ }
    return ''
  })
  const [shots, setShots] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  // Try to autofill version from /api/me if available.
  useEffect(() => {
    let alive = true
    fetch('/api/me').then(r => r.json()).then(info => {
      if (!alive) return
      if (info?.version && !affectsVersion) setAffectsVersion(String(info.version))
    }).catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = async () => {
    if (!title.trim()) return
    setError('')
    setSending(true)
    try {
      // Append project URL to description with a special marker so the
      // frontend can extract it back out and show it only to admins on
      // the detail page. Other clients won't see this line — see the
      // _stripPrivateMarkers helper used in BugDetailPage.
      let desc = description.trim()
      const purl = projectUrl.trim()
      if (purl) {
        const marker = `<!--PROJECT_URL:${purl}-->`
        desc = desc ? `${desc}\n\n${marker}` : marker
      }
      const r = await bugsCreate({
        title: title.trim(),
        description: desc,
        steps_to_reproduce: steps.trim(),
        expected: expected.trim(),
        actual: actual.trim(),
        severity,
        affects_version: affectsVersion.trim(),
        browser_info: browserInfo.trim(),
        category,
        author_label: authorLabel.trim(),
        screenshots: shots,
      })
      if (r.ok) {
        onCreated()
      } else {
        setError(r.error || 'Не удалось отправить баг-репорт')
      }
    } catch (e: any) {
      setError(e?.message || 'Ошибка сети')
    }
    setSending(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-card border border-border rounded-xl w-full max-w-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-400" /> Новый баг-репорт
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Заголовок <span className="text-red-400">*</span></label>
          <Input value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="Кратко о баге (напр. «После рестарта панели сбрасывается VPN»)"
                 maxLength={200} />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Описание</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
                    rows={3} maxLength={4000}
                    placeholder="Контекст проблемы, когда это случилось, что именно не так..."
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[80px]" />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Шаги воспроизведения</label>
          <textarea value={steps} onChange={e => setSteps(e.target.value)}
                    rows={3} maxLength={4000}
                    placeholder={'1. Открыть…\n2. Нажать…\n3. Увидеть ошибку'}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[80px] font-mono" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Ожидаемое поведение</label>
            <textarea value={expected} onChange={e => setExpected(e.target.value)}
                      rows={3} maxLength={2000}
                      placeholder="Что должно было произойти"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[70px]" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Фактическое поведение</label>
            <textarea value={actual} onChange={e => setActual(e.target.value)}
                      rows={3} maxLength={2000}
                      placeholder="Что произошло вместо этого"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[70px]" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Категория</label>
            <Select value={category} onChange={setCategory}
                    options={Object.entries(CATEGORIES).map(([id, label]) => ({ id, label }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Приоритет</label>
            <Select value={severity} onChange={setSeverity}
                    options={Object.entries(SEVERITY_META).map(([id, m]) => ({ id, label: m.label }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Версия</label>
            <Input value={affectsVersion} onChange={e => setAffectsVersion(e.target.value)}
                   placeholder="1.0.356" maxLength={60} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Окружение/браузер</label>
            <Input value={browserInfo} onChange={e => setBrowserInfo(e.target.value)}
                   placeholder="User agent или OS" maxLength={300} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Ваше имя <span className="text-muted-foreground">(опц.)</span></label>
            <Input value={authorLabel} onChange={e => setAuthorLabel(e.target.value)}
                   placeholder="Как подписать" maxLength={60} />
          </div>
        </div>

        {/* Operator request: link to the exact project where the bug
            was seen, so triage knows which campaign settings to inspect.
            VISIBLE ONLY TO TEAM — фильтруется в BugDetailPage для не-админов. */}
        <div className="space-y-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-3">
          <label className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5">
            🔒 Ссылка на проект <span className="text-muted-foreground font-normal">(только для команды, опц.)</span>
          </label>
          <Input
            type="url"
            value={projectUrl}
            onChange={e => setProjectUrl(e.target.value)}
            placeholder="https://stormdi.crp.pro/project/abc123…"
            maxLength={500}
          />
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            <b>Эту ссылку видим только мы (команда разработчиков)</b> — другие клиенты её не увидят
            в карточке бага. Помогает быстро открыть твои настройки проекта, чтобы воспроизвести проблему.
            Скопируй URL из адресной строки браузера со страницы настроек проекта.
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Скриншоты <span className="text-muted-foreground">(до {MAX_SHOTS_BUG}, опц.)</span></label>
          <ScreenshotUploader value={shots} onChange={setShots} max={MAX_SHOTS_BUG} disabled={sending} />
        </div>

        {error && <div className="text-xs text-red-400">{error}</div>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={onSubmit} disabled={!title.trim() || sending} className="gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Отправить
          </Button>
        </div>
      </div>
    </div>
  )
}


function Select({ value, onChange, options, placeholder }: {
  value: string
  onChange: (v: string) => void
  options: { id: string; label: string }[]
  placeholder?: string
}) {
  return (
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)}
              className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-background text-sm cursor-pointer hover:border-primary/30 w-full">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
    </div>
  )
}


// Idea #35: edit your own bug
function BugEditModal({ bug, onClose, onSaved }: {
  bug: BugType; onClose: () => void; onSaved: () => void
}) {
  // Strip private PROJECT_URL marker before edit so the user doesn't see
  // their own marker in the textarea — we re-attach on save if URL given.
  const initMarker = (bug.description || '').match(/<!--PROJECT_URL:(.+?)-->/)
  const initProjectUrl = initMarker ? initMarker[1].trim() : ''
  const cleanInitDesc = (bug.description || '').replace(/\s*<!--PROJECT_URL:.+?-->\s*/g, '').trim()

  const [title, setTitle] = useState(bug.title)
  const [description, setDescription] = useState(cleanInitDesc)
  const [steps, setSteps] = useState(bug.steps_to_reproduce || '')
  const [expected, setExpected] = useState(bug.expected || '')
  const [actual, setActual] = useState(bug.actual || '')
  const [severity, setSeverity] = useState(bug.severity)
  const [projectUrl, setProjectUrl] = useState(initProjectUrl)
  const [shots, setShots] = useState<string[]>(
    // bug.screenshots are full URLs; for re-save we need the bare filename.
    (bug.screenshots || []).map(u => {
      try {
        const m = u.match(/\/([^\/]+)$/)
        return m ? m[1] : u
      } catch { return u }
    })
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!title.trim()) return
    setError(''); setSaving(true)
    try {
      let desc = description.trim()
      const purl = projectUrl.trim()
      if (purl) desc = desc ? `${desc}\n\n<!--PROJECT_URL:${purl}-->` : `<!--PROJECT_URL:${purl}-->`
      await bugsUpdateOwn(bug.id, {
        title: title.trim(),
        description: desc,
        steps_to_reproduce: steps.trim(),
        expected: expected.trim(),
        actual: actual.trim(),
        severity,
        screenshots: shots,
      })
      onSaved()
    } catch (e: any) {
      setError('Не удалось сохранить: ' + (e?.message || e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-background rounded-xl border border-border max-w-2xl w-full p-5 space-y-3 my-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Pencil className="w-5 h-5 text-primary" /> Редактирование бага #{bug.id}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Заголовок *</label>
          <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium">Описание</label>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} maxLength={5000} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Шаги воспроизведения</label>
            <Textarea value={steps} onChange={e => setSteps(e.target.value)} rows={3} maxLength={2000} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Серьёзность</label>
            <Select
              value={severity}
              onChange={(v) => setSeverity(v as BugSeverity)}
              options={[
                { id: 'low', label: 'Низкая' },
                { id: 'medium', label: 'Средняя' },
                { id: 'high', label: 'Высокая' },
                { id: 'critical', label: 'Критичная' },
              ]}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Ожидаемое</label>
            <Textarea value={expected} onChange={e => setExpected(e.target.value)} rows={3} maxLength={2000} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Фактическое</label>
            <Textarea value={actual} onChange={e => setActual(e.target.value)} rows={3} maxLength={2000} />
          </div>
        </div>

        <div className="space-y-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-3">
          <label className="text-xs font-semibold text-emerald-400">
            🔒 Ссылка на проект <span className="text-muted-foreground font-normal">(только для команды)</span>
          </label>
          <Input type="url" value={projectUrl} onChange={e => setProjectUrl(e.target.value)} placeholder="https://...crp.pro/project/..." />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Скриншоты</label>
          <ScreenshotUploader value={shots} onChange={setShots} max={MAX_SHOTS_BUG} disabled={saving} />
        </div>

        {error && <div className="text-xs text-red-400">{error}</div>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={handleSave} disabled={!title.trim() || saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Сохранить
          </Button>
        </div>
      </div>
    </div>
  )
}


// Idea #35: reopen a closed bug — mandatory comment
function BugReopenDialog({ onClose, onConfirm }: {
  onClose: () => void; onConfirm: (text: string) => Promise<void>
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-amber-400" /> Возобновить тикет
          </AlertDialogTitle>
          <AlertDialogDescription>
            Расскажите, что именно осталось не работающим. Тикет получит статус «Новый»,
            а ваш текст добавится комментарием для команды.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Что именно не работает / какая ошибка осталась…"
          maxLength={2000}
          className="text-sm"
          autoFocus
        />
        <div className="text-[11px] text-muted-foreground">
          Минимум 10 символов · {text.length} / 2000
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            disabled={text.trim().length < 10 || busy}
            onClick={async (e) => {
              e.preventDefault()
              setBusy(true)
              try {
                await onConfirm(text.trim())
              } finally {
                setBusy(false)
              }
            }}
            className="gap-2 bg-amber-600 hover:bg-amber-700"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Возобновить
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
